import type { PropsWithChildren } from "react"
import React from "react"
import CookieConsent from "react-cookie-consent"
import { Helmet } from "react-helmet"
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies"

import { CallToAction } from "../cta/CallToAction"
import { Header } from "./Header"
import { Footer } from "./Footer"

export type PageProps = PropsWithChildren<{
  header: string
  title: string
  description: string
  keywords: string
  slug?: string
}>

export const Page = (props: PageProps) => {
  const {
    children,
    header,
    title,
    description: suppliedDescription,
    keywords,
    slug,
  } = props

  const docTitle = `${title} - XRPLGrants.org`
  const description =
    suppliedDescription ||
    `The XRPL Grants program funds software development projects that leverage the open-source XRP Ledger (XRPL).`
  const image = `https://xrplgrants.org/images/metadata.png`

  const metadata = [
    {
      charSet: `utf-8`,
    },
    {
      property: `og:title`,
      content: docTitle,
    },
    {
      property: `og:description`,
      content: description,
    },
    {
      property: `og:image`,
      content: image,
    },
    {
      property: `description`,
      content: description,
    },
    {
      property: `keywords`,
      content: keywords,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:site`,
      content: `@RippleXDev`,
    },
    {
      name: `twitter:title`,
      content: docTitle,
    },
    {
      name: `twitter:description`,
      content: description,
    },
    {
      name: `twitter:image`,
      content: image,
    },
  ]

  return (
    <div className={`page-${slug}`}>
      <Header />
      <CookieConsent
        disableStyles
        flipButtons
        cookieName="gatsby-gdpr-google-tagmanager"
        containerClasses="fixed flex justify-center items-center left-0 xs:left-auto right-0 m-2 p-1 rounded-lg bg-white z-50	"
        enableDeclineButton
        buttonClasses="btn btn-primary"
        buttonText="Accept"
        declineButtonClasses="btn btn-secondary"
        declineButtonText="Decline"
        buttonWrapperClasses="flex justify-between gap-[10px] ml-[10px]"
        onAccept={() => {
          initializeAndTrack(location)
        }}
      >
        <p>This website uses cookies to enhance the user experience. </p>
      </CookieConsent>
      <Helmet title={docTitle} meta={metadata}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Work+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,700;1,900&display=swap"
          rel="stylesheet"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/yellow_background.svg"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/orange_background.svg"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/purple_background.svg"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/red_purple_background.svg"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/magenta_background.svg"
        />
        <link
          rel="preload"
          as="image"
          href="/images/backgrounds/blue_background.svg"
        />
      </Helmet>
      <div className="page-body">
        <h1 className="md:text-center">{header}</h1>
        <div className="page-content">{children}</div>
      </div>
      <CallToAction />
      <Footer />
    </div>
  )
}
